import { isNullOrEmpty } from 'common-utils';
import { PermissionDenied } from 'pages/private/components/notifications/permission-notification';
import React from 'react';
import { useStores } from 'store';

export interface IUsePermissionProps {
    availablePermissionCodes: string[];
    hasPermission: (permissionCode: string) => boolean;
    doWithPermission: (
        permissionCode: string,
        action: () => Promise<unknown>,
        onMissingPermission?: () => unknown,
    ) => Promise<unknown>;
}
interface PermissionProviderProps {
    permissionGroups: string[];
}

export const usePermission = (props: PermissionProviderProps): IUsePermissionProps => {
    const { permissionGroups } = props;
    const { permissionStore } = useStores();

    const permissionCodes = React.useMemo(() => {
        if (isNullOrEmpty(permissionStore.getPermissions)) return [];
        return permissionStore.getPermissions
            .filter((permission) => permissionGroups.includes(permission.group))
            .map((permission) => permission.code);
    }, [permissionStore.getPermissions]);

    const hasPermission = React.useCallback((permissionCode: string) => permissionCodes.includes(permissionCode), [permissionCodes]);

    const doWithPermission = React.useCallback((
        permissionCode: string,
        action: () => Promise<unknown>,
        onMissingPermission?: () => unknown,
    ): Promise<unknown> => {
        const onFallback =
            onMissingPermission ??
            (() => PermissionDenied(`You don't have permission ${permissionCode} to do this action.`));
        if (!hasPermission(permissionCode)) {
            return Promise.resolve(onFallback());
        }
        return permissionStore.doWithPermission(permissionCode, action, {
            onError: onFallback,
        });
    }, [hasPermission]);

    return {
        availablePermissionCodes: permissionCodes,
        hasPermission,
        doWithPermission,
    };
};
